import { CHECKLIST_ITEMS } from '@/db/checklist'
import { summarizeInspectionResults } from '@/db/rules'
import type { Inspection, InspectionResult, Photo, PhotoPreview, Signature } from '@/db/types'

export type CompletionTarget =
  | 'report'
  | 'checklist'
  | 'diagnosis'
  | 'authorization'
  | 'responsible'
  | 'client-signature'
  | 'photos'

export interface CompletionIssue {
  code: string
  message: string
  target: CompletionTarget
  systemOrder?: number
  resultId?: string
}

export interface CompletionRelationships {
  clientExists: boolean
  machineExists: boolean
  technicianExists: boolean
  machineBelongsToClient: boolean
}

export interface CompletionValidationInput {
  inspection: Inspection
  results: readonly InspectionResult[]
  clientSignature?: Signature
  technicianSignature?: Signature
  photos: readonly Photo[]
  previews: readonly PhotoPreview[]
  relationships: CompletionRelationships
}

export function validateInspectionForCompletion({
  inspection,
  results,
  clientSignature,
  technicianSignature,
  photos,
  previews,
  relationships,
}: CompletionValidationInput): CompletionIssue[] {
  const issues: CompletionIssue[] = []
  if (!relationships.clientExists) {
    issues.push({
      code: 'client_missing',
      message: 'Cliente vinculado não encontrado.',
      target: 'report',
    })
  }
  if (!relationships.machineExists) {
    issues.push({
      code: 'machine_missing',
      message: 'Máquina vinculada não encontrada.',
      target: 'report',
    })
  }
  if (!relationships.technicianExists) {
    issues.push({
      code: 'technician_missing',
      message: 'Técnico vinculado não encontrado.',
      target: 'report',
    })
  }
  if (relationships.machineExists && !relationships.machineBelongsToClient) {
    issues.push({
      code: 'machine_client_mismatch',
      message: 'A máquina não pertence ao cliente da inspeção.',
      target: 'report',
    })
  }
  if (!Number.isFinite(inspection.hourmeter) || inspection.hourmeter < 0) {
    issues.push({ code: 'hourmeter_invalid', message: 'Horímetro inválido.', target: 'report' })
  }

  const resultByItem = new Map(results.map((result) => [result.checklistItemId, result]))
  for (const item of CHECKLIST_ITEMS) {
    const result = resultByItem.get(item.id)
    if (!result || result.status === 'unanswered') {
      issues.push({
        code: `pending_${item.id}`,
        message: `${item.id} — ${item.label}`,
        target: 'checklist',
        systemOrder: item.systemOrder,
        ...(result ? { resultId: result.id } : {}),
      })
    }
  }
  const summary = summarizeInspectionResults(results)
  if ((summary.attention > 0 || summary.critical > 0) && !inspection.diagnosis.trim()) {
    issues.push({
      code: 'diagnosis_required',
      message: 'Informe o diagnóstico técnico para os itens em Atenção ou Crítico.',
      target: 'diagnosis',
    })
  }
  if (inspection.authorization === 'unset' || !inspection.authorizationAt) {
    issues.push({
      code: 'authorization_required',
      message: 'Selecione Sim ou Não na autorização.',
      target: 'authorization',
    })
  }
  if (!inspection.responsibleName.trim()) {
    issues.push({
      code: 'responsible_required',
      message: 'Informe o nome do cliente ou responsável.',
      target: 'responsible',
    })
  }

  const validClientSignature = Boolean(
    clientSignature &&
    clientSignature.id === inspection.clientSignatureId &&
    clientSignature.inspectionId === inspection.id &&
    clientSignature.type === 'client' &&
    clientSignature.blob.size > 0,
  )
  if (inspection.authorization === 'yes' && !validClientSignature) {
    issues.push({
      code: 'client_signature_required',
      message: 'A autorização Sim exige a assinatura confirmada do cliente.',
      target: 'client-signature',
    })
  }
  if (
    inspection.authorization === 'no' &&
    !validClientSignature &&
    (!inspection.clientSignatureWaiverReason.trim() || !inspection.clientSignatureWaiverConfirmedAt)
  ) {
    issues.push({
      code: 'signature_waiver_required',
      message: 'Registre e confirme a justificativa quando o cliente não assinar.',
      target: 'client-signature',
    })
  }
  if (
    inspection.technicianSignatureId &&
    (!technicianSignature ||
      technicianSignature.id !== inspection.technicianSignatureId ||
      technicianSignature.inspectionId !== inspection.id ||
      technicianSignature.type !== 'technician' ||
      technicianSignature.blob.size === 0)
  ) {
    issues.push({
      code: 'technician_signature_invalid',
      message: 'A assinatura opcional do técnico está inconsistente.',
      target: 'client-signature',
    })
  }

  const previewByPhoto = new Map(previews.map((preview) => [preview.photoId, preview]))
  const resultIds = new Set(results.map((result) => result.id))
  for (const photo of photos) {
    const preview = previewByPhoto.get(photo.id)
    const invalidPhoto =
      photo.inspectionId !== inspection.id ||
      photo.blob.size === 0 ||
      photo.blob.size !== photo.size ||
      !preview ||
      preview.inspectionId !== inspection.id ||
      preview.category !== photo.category ||
      preview.resultId !== photo.resultId ||
      preview.fullSize !== photo.size ||
      preview.thumbnailBlob.size !== preview.thumbnailSize ||
      (photo.category === 'item' && (!photo.resultId || !resultIds.has(photo.resultId))) ||
      (photo.category === 'general' && Boolean(photo.resultId))
    if (invalidPhoto) {
      issues.push({
        code: `photo_invalid_${photo.id}`,
        message: `A evidência fotográfica ${photo.order + 1} está inconsistente.`,
        target: 'photos',
      })
    }
  }
  for (const preview of previews) {
    if (!photos.some((photo) => photo.id === preview.photoId)) {
      issues.push({
        code: `preview_orphan_${preview.id}`,
        message: 'Há uma miniatura sem a foto original correspondente.',
        target: 'photos',
      })
    }
  }
  return issues
}
